import { GenericModal } from "@Shared/components/modals";
import { showToast } from "@Shared/@Utils/constants";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength } from "vuelidate/lib/validators";
import SimpleButton from "@Shared/components/buttons/simple-button.vue";
import MissingDetailsStep from "@Broker/@Modules/clients/applications/application/applications-list/components/import-clients/steps/missing-details-step.vue";
import VerifyEmailStep from "@Broker/@Modules/clients/applications/application/applications-list/components/import-clients/steps/verify-email-step.vue";
import Amplitude from "@Shared/@Utils/amplitude";
import SyncAgentFromExpertModalLoader from "./sync-agent-from-expert-modal-loader.vue";
import { defineComponent, ref, getCurrentInstance, computed } from "@vue/composition-api";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import * as SettingsActions from "@Broker/@Modules/settings/store/settings-actions";
import * as ApplicationActions from "@Broker/@Modules/clients/applications/application/store/application-actions";
import getFilogixDetails from "@Broker/@Composables/generic/getFilogixDetails";
import missingFilogixDetails from "@Broker/@Composables/generic/missingFilogixDetails";
const {
  useActions: useSettingsActions
} = createNamespacedHelpers("SettingsStore");
const {
  useActions: useApplicationActions
} = createNamespacedHelpers("ApplicationStore");
export default defineComponent({
  name: "SyncAgentFromExpertModal",
  components: {
    GenericModal,
    SimpleButton,
    MissingDetailsStep,
    VerifyEmailStep,
    SyncAgentFromExpertModalLoader
  },
  mixins: [validationMixin],
  validations() {
    return {
      filogixDetailsLocal: {
        firmCode: {
          required,
          minLength: minLength(4)
        },
        expertLoginId: {
          required
        },
        expertProfileNumber: {
          required
        }
      },
      emailVerificationCode: {
        required: requiredIf(() => {
          return this.confirmPullingInfo && !this.missingFilogixDetailsForImport && !this.invalidExpertProfileNumber && !this.isExpertNumberVerified;
        })
      }
    };
  },
  setup() {
    const internalInstance = getCurrentInstance();
    const {
      filogixDetails
    } = getFilogixDetails();
    const {
      missingFilogixDetailsForImport
    } = missingFilogixDetails(filogixDetails, ["firmCode", "expertLoginId", "expertProfileNumber"]);
    let invalidExpertProfileNumber = ref(false);
    let confirmPullingInfo = ref(false);
    let isExpertNumberVerified = ref(false);
    const btnText = computed(() => {
      if (missingFilogixDetailsForImport.value || invalidExpertProfileNumber.value) return internalInstance == null ? void 0 : internalInstance.proxy.$t("global.words.submit");
      if (confirmPullingInfo.value && !isExpertNumberVerified.value) return internalInstance == null ? void 0 : internalInstance.proxy.$t("settings.accountTab.verifyImportButtonText");
      return internalInstance == null ? void 0 : internalInstance.proxy.$t("global.words.sync");
    });
    let filogixDetailsLocal = ref({});
    const handleShow = () => {
      filogixDetailsLocal.value = filogixDetails.value;
      invalidExpertProfileNumber.value = false;
      if (missingFilogixDetailsForImport.value) confirmPullingInfo.value = true;
    };
    const handleHide = () => {
      confirmPullingInfo.value = false;
    };
    const filogixDetailsChange = value => {
      filogixDetailsLocal.value = value;
    };
    let emailVerificationCode = ref("");
    const updateEmailVerificationCode = value => {
      emailVerificationCode.value = value;
    };
    let loading = ref(false);
    let loadingSucceeded = ref(true);
    const handleSync = async () => {
      loading.value = true;
      try {
        if (missingFilogixDetailsForImport.value || invalidExpertProfileNumber.value) await updateFilogixDetails();
        if (!isExpertNumberVerified.value && emailVerificationCode.value) await verifyFilogixInfo();
        await loadSubmissionAgents();
        if (isExpertNumberVerified.value) notifySuccess();
        loadingSucceeded.value = true;
      } catch (e) {
        loadingSucceeded.value = false;
        Amplitude.logHandledError(e);
      } finally {
        loading.value = false;
        confirmPullingInfo.value = true;
      }
    };
    const {
      UPDATE_FILOGIX_DETAILS,
      VERIFY_EXPERT_PROFILE_NUMBER
    } = useSettingsActions([SettingsActions.UPDATE_FILOGIX_DETAILS, SettingsActions.VERIFY_EXPERT_PROFILE_NUMBER]);
    const updateFilogixDetails = async () => {
      await UPDATE_FILOGIX_DETAILS(filogixDetailsLocal.value);
    };
    const verifyFilogixInfo = async () => {
      isExpertNumberVerified.value = await VERIFY_EXPERT_PROFILE_NUMBER(emailVerificationCode.value);
      emailVerificationCode.value = "";
    };
    const {
      SYNC_AGENT_FROM_EXPERT
    } = useApplicationActions([ApplicationActions.SYNC_AGENT_FROM_EXPERT]);
    let showLoader = ref(false);
    let verificationEmail = ref("");
    const loadSubmissionAgents = async () => {
      showLoader.value = true;
      try {
        const agentDetails = await SYNC_AGENT_FROM_EXPERT();
        isExpertNumberVerified.value = !(agentDetails != null && agentDetails.shouldVerify);
        verificationEmail.value = agentDetails == null ? void 0 : agentDetails.emailFromExpert;
        invalidExpertProfileNumber.value = false;
      } catch (e) {
        invalidExpertProfileNumber.value = true;
        Amplitude.logHandledError(e);
      } finally {
        showLoader.value = false;
      }
    };
    const notifySuccess = () => {
      showToast("success", "importHasBegunMessage");
      (internalInstance == null ? void 0 : internalInstance.proxy).$hideModal();
    };
    return {
      invalidExpertProfileNumber,
      confirmPullingInfo,
      isExpertNumberVerified,
      filogixDetailsLocal,
      emailVerificationCode,
      verificationEmail,
      loading,
      loadingSucceeded,
      showLoader,
      missingFilogixDetailsForImport,
      btnText,
      handleShow,
      handleHide,
      filogixDetailsChange,
      updateEmailVerificationCode,
      handleSync
    };
  }
});